import { StoreKey } from "lib/Intl";
import getConfig from "next/config";
import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { useSession } from "next-auth/client";
import { useCart } from "src/hooks/useCart";
import { useCreateCart } from "src/hooks/useCreateCart";
import { useSetCartCustomerEmail } from "src/hooks/useSetCartCustomerEmail";
import { CartInfo } from "src/types/cartInfo";
import { createReducer, type Dispatch } from "src/utils/create-reducer";
import { getCartInfo, getCartItemsCount } from "src/utils/ct/cart-helper";
import { storeConfigs } from "src/utils/storeConfig";

export type CartState = {
  id: string;
  version: number;
  totalItems: number;
  shouldCreateNewCart?: boolean;
};

const initialState: CartState = {
  id: "",
  version: 0,
  totalItems: 0,
  shouldCreateNewCart: false,
};

export type CartActions =
  | { type: "SET_CART_STATE"; payload: CartState }
  | {
      type: "SET_CART_INFO";
      payload: CartInfo;
    }
  | {
      type: "SET_CART_VERSION";
      payload: number;
    }
  | {
      type: "SET_TOTAL_ITEMS";
      payload: number;
    };

export type UseCartContextReturn = {
  cartState: CartState;
  cartContextDispatch: Dispatch<CartActions>;
};

export const CartContext = createContext<CartState | undefined>(undefined);

export const CartContextDispatch = createContext<
  Dispatch<CartActions> | undefined
>(undefined);

export const cartReducer = createReducer<CartState, CartActions>({
  SET_CART_STATE: (_, payload) => ({
    ...payload,
  }),
  SET_CART_INFO: (state, payload) => ({
    ...state,
    ...payload,
  }),
  SET_CART_VERSION: (state, payload) => ({
    ...state,
    version: payload,
  }),
  SET_TOTAL_ITEMS: (state, payload) => ({
    ...state,
    totalItems: payload,
  }),
});

const { publicRuntimeConfig } = getConfig();

export function CartContextProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [cartState, cartContextDispatch] = useReducer(
    cartReducer,
    initialState
  );
  const [session] = useSession();

  const createCarteRef = useRef(true);
  const { createCart } = useCreateCart({
    refetchQueries: ["cart"],
    onCompleted: (cartData) => {
      cartContextDispatch({
        type: "SET_CART_STATE",
        payload: {
          ...getCartInfo(cartData?.createMyCart),
          totalItems: getCartItemsCount(
            cartData?.createMyCart?.lineItems || []
          ),
          shouldCreateNewCart: false,
        },
      });
    },
  });

  const { setCartCustomerEmail } = useSetCartCustomerEmail({
    refetchQueries: ["cart"],
  });

  useCart({
    onCompleted: (cartData) => {
      if (createCarteRef.current && !cartData.inStore?.me?.activeCart) {
        // Make sure a cart is created when the user doesn't have one yet
        void createCart({
          currency:
            storeConfigs[
              (publicRuntimeConfig.STORE_KEY as StoreKey) || "OT-COM"
            ].currencyCode,
        });

        createCarteRef.current = false;
        // Cart query is refetch once create cart is completed
        return;
      }

      const cart = cartData.inStore?.me?.activeCart;

      // If cart exists but has no customerEmail, and we have user email, set it
      if (cart?.id && cart?.customerId && session?.user?.email) {
        const sessionEmail = session.user.email.trim().toLowerCase();
        const cartEmail = (cart.customerEmail ?? "").trim().toLowerCase();

        if (!cartEmail || cartEmail !== sessionEmail) {
          void setCartCustomerEmail(sessionEmail, {
            id: cart.id,
            version: cart.version as number,
          });
        }
      }

      cartContextDispatch({
        type: "SET_CART_STATE",
        payload: {
          ...getCartInfo(cartData.inStore?.me?.activeCart),
          totalItems: getCartItemsCount(
            cartData.inStore?.me?.activeCart?.lineItems || []
          ),
          shouldCreateNewCart: false,
        },
      });
    },
  });

  const handleCreateCart = useCallback(async () => {
    await createCart({
      currency:
        storeConfigs[(publicRuntimeConfig.STORE_KEY as StoreKey) || "OT-COM"]
          .currencyCode,
    });
  }, [createCart]);

  useEffect(() => {
    if (!cartState.shouldCreateNewCart) {
      return;
    }

    void handleCreateCart();
  }, [cartState.shouldCreateNewCart, handleCreateCart]);

  const cartContextValue = useMemo(() => cartState, [cartState]);

  return (
    <CartContext.Provider value={cartContextValue}>
      <CartContextDispatch.Provider value={cartContextDispatch}>
        {children}
      </CartContextDispatch.Provider>
    </CartContext.Provider>
  );
}
